/**
 * Identidade fiscal do destinatário da NF-e (dest/indIEDest + dest/IE).
 *
 * Espelha a regra do backend (ERPApi `ResolveDestFiscalProfile`, internal/core/services/
 * nfe_dest_fiscal.go), que é quem decide de fato o que vai no XML: a tela apenas pré-preenche
 * a partir do cadastro do cliente para o operador conferir. Na emissão o backend ainda consulta
 * o cadastro de contribuintes da SEFAZ (CadConsultaCadastro4) e, se o valor enviado divergir do
 * resolvido, retorna o erro DEST_FISCAL_DIVERGENCE com os valores corretos em `fields` — a tela
 * aplica esses valores e o operador reemite (nada é sobrescrito em silêncio).
 */

export const IND_IE_DEST_CONTRIBUINTE = 1;
export const IND_IE_DEST_ISENTO = 2;
export const IND_IE_DEST_NAO_CONTRIBUINTE = 9;

/**
 * UFs que não aceitam indIEDest=2 (contribuinte isento): lista da rejeição 805 (NT 2015/003)
 * + DF, onde toda empresa possui CF/DF tratado pela SEFAZ como IE ativa (tanto 2 quanto 9 são
 * rejeitados com cStat 232 — o correto lá é 1 + nº do CF/DF).
 * Espelha `ufsRejectingIsentoDest` do backend.
 */
export const UFS_REJEITAM_ISENTO = new Set([
  'AM', 'BA', 'CE', 'DF', 'GO', 'MG', 'MS', 'MT', 'PA', 'PE', 'RN', 'SE', 'SP',
]);

/** UF vazia/desconhecida aceita isento (lista de negação — comportamento antigo mantido). */
export function ufAceitaIsento(uf?: string | null): boolean {
  return !UFS_REJEITAM_ISENTO.has((uf || '').trim().toUpperCase());
}

export interface DestFiscalProfile {
  /** 1 = Contribuinte ICMS, 2 = Isento, 9 = Não contribuinte */
  contribuinte: number;
  /** Só dígitos; vazia quando contribuinte != 1 (a NF-e omite a tag <IE>) */
  ie: string;
}

export interface DestFiscalSource {
  /** CPF/CNPJ em qualquer formato */
  document?: string | null;
  /** Customer.StateRegistrationTaxId */
  stateRegistrationTaxId?: string | null;
  /** Customer.NonContributorStateRegistrationTaxId ("Isento de Inscrição Estadual") */
  nonContributorIE?: boolean | null;
}

/**
 * Deriva contribuinte/IE do cadastro do cliente, ciente da UF de destino.
 *
 * Ordem (igual ao backend): IE com dígitos vence a flag de isento → 1 + IE; flag de isento só
 * vale 2 em UF que aceita (`ufAceitaIsento`), senão 9. Pessoa física (CPF) cai sempre em
 * "não contribuinte" com a IE omitida — é o caso dominante do e-commerce e evita que um valor
 * residual no campo IE de um CPF vire indIEDest=1.
 */
export function resolveDestFiscal(source: DestFiscalSource, destUF?: string | null): DestFiscalProfile {
  const docDigits = (source.document || '').replace(/\D/g, '');
  const isCNPJ = docDigits.length === 14;

  if (!isCNPJ) {
    return { contribuinte: IND_IE_DEST_NAO_CONTRIBUINTE, ie: '' };
  }

  // A IE pode vir formatada ou como o literal "ISENTO" em alguns cadastros: sem dígitos, o
  // destinatário não é tratado como contribuinte.
  const ieDigits = (source.stateRegistrationTaxId || '').replace(/\D/g, '');
  if (ieDigits) {
    return { contribuinte: IND_IE_DEST_CONTRIBUINTE, ie: ieDigits };
  }
  if (source.nonContributorIE && ufAceitaIsento(destUF)) {
    return { contribuinte: IND_IE_DEST_ISENTO, ie: '' };
  }
  return { contribuinte: IND_IE_DEST_NAO_CONTRIBUINTE, ie: '' };
}
